import React,{useEffect, useState, useRef,useMemo,Fragment} from "react";
import moment from "moment";
import axios from "../../plugins/axios";
import { useRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import { selectBedInfoState,globalParameterColorState,isFromSearchState,demoModeState } from "../../plugins/recoil";

import {ReactComponent as IconArrowUp} from '../../assets/icons/ic-arrow-up.svg'
import {ReactComponent as IconArrowDown} from '../../assets/icons/ic-arrow-down.svg'
import {ReactComponent as IconCheck} from '../../assets/icons/ic-check_blue.svg';
import { ReactComponent as IconPrint } from '../../assets/icons/ic-print.svg'
import backgroundImage from '../../assets/img/chart_bg_gray.svg';
import Dropdown from './Dropdown';

const Strip = ({ isTrendFull, onClose, selectBoxTimeInfo,patientHistories, showStripPrint }) => {
    const { t } = useTranslation()
    const areaRefs = useRef([])
    const waveAreaContainerRef = useRef(null);
    const speedDropdownRef = useRef(null);    
    const [selectBedInfo] = useRecoilState(selectBedInfoState)
    const [parameterColors, ] = useRecoilState(globalParameterColorState);
    const [isFromSearch, ] = useRecoilState(isFromSearchState)
    const [isDemoMode,] = useRecoilState(demoModeState)
    const [waveStartTime, setWaveStartTime] = useState()
    const [waveEndTime, setWaveEndTime] = useState()
    const [chartWidth, setChartWidth] = useState(0)
    const [chartHeight, setChartHeight] = useState(0)
    const [dragging, setDragging] = useState(false)            
    const [stripedBackground, setStripedBackground] = useState(); // 빗금 배경
    const [patientLines, setPatientLines] = useState(); // 빗금 위치 상태 추가
    const [startPosition, setStartPosition] = useState(null)
    const [currentPosition, setCurrentPosition] = useState(null)
    const [endPosition, setEndPosition] = useState(null)
    const [activeWaveIndex, setActiveWaveIndex] = useState(null)
    const [elapsedTime, setElapsedTime] = useState(0)
    const [waveCodeList, setWaveCodeList] = useState([])
    const [waveParameters, setWaveParameters] = useState([])
    const [isSpeedDropdownOpen, setIsSpeedDropdownOpen] = useState(false);
    const [speed, setSpeed] = useState({ label: '25mm/s', value: 25 })    
    const speedOptions = [
        { label: '6.25 mm/s', value: 6.25 },
        { label: '12.5 mm/s', value: 12.5 },
        { label: '25.0 mm/s', value: 25.0 },
        { label: '50.0 mm/s', value: 50.0 },
    ];
    
    const [dropdownStates, setDropdownStates] = useState({
        wave1: false,
        wave2: false,
        wave3: false,
        wave4: false
    });

    const [waveDatas, setWaveDatas] = useState({
        wave1: [],
        wave2: [],
        wave3: [],
        wave4: []
    })

    const [waveYAxisRange, setWaveYAxisRange] = useState({
        wave1: { min: 0, max: 100 },
        wave2: { min: 0, max: 100 },
        wave3: { min: 0, max: 100 },
        wave4: { min: 0, max: 100 }
    });

    const [wavePoints, setWavePoints] = useState({
        wave1: [],
        wave2: [],
        wave3: [],
        wave4: []
    })
    const [scrollLeft, setScrollLeft] = useState(0);
    const [hoveredPosition, setHoveredPosition] = useState(null);

    const pixelsPerSecond = useMemo(() => {
        const basePixelsPerSecond = 90; // 25mm/s 기준 (90픽셀 = 25mm)
        const baseSpeed = 25; // 기준 속도 25mm/s
        return (basePixelsPerSecond * (speed.value || baseSpeed)) / baseSpeed;
    }, [speed]);
    
    // Wave Code List 가져오기
    const getWaveCodeList = async () => {
        try {
            const waveCodeRes = await axios.get(`/measure/parameters/wave`);
            if (waveCodeRes.code != '200') throw waveCodeRes;            
            setWaveCodeList(waveCodeRes.data.parameters);
            const lead2 = waveCodeRes.data.parameters.find(item => item.parameter === 'LEADⅡ');
            const pleth = waveCodeRes.data.parameters.find(item => item.parameter === 'PLETH');
            const resp = waveCodeRes.data.parameters.find(item => item.parameter === 'RESP');
            const ibp1 = waveCodeRes.data.parameters.find(item => item.parameter === 'IBP1');
            if (isTrendFull) {
                setWaveParameters([
                    { measureParameterId: lead2.id, measureParameter: lead2.parameter, measureParameterName: lead2.name },
                    { measureParameterId: pleth.id, measureParameter: pleth.parameter, measureParameterName: pleth.name },
                    { measureParameterId: resp.id, measureParameter: resp.parameter, measureParameterName: resp.name },
                    { measureParameterId: ibp1.id, measureParameter: ibp1.parameter, measureParameterName: ibp1.name }
                ])
            } else {
                setWaveParameters([
                    { measureParameterId: lead2.id, measureParameter: lead2.parameter, measureParameterName: lead2.name },
                    { measureParameterId: pleth.id, measureParameter: pleth.parameter, measureParameterName: pleth.name }
                ])
            }            
        } catch (err) { console.warn(err) }
    }

    const getTrendWave = async () => {
        try{
            const newWaveDatas = {};
        
            const waveDataPromises = waveParameters.map(async (item, idx) => {                
                const res = await axios.get(`/trend/waveforms?serialNo=${selectBedInfo.serial}&type=${item.measureParameter}&startTime=${moment(waveStartTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment(waveEndTime).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`);                
                if (res.code != '200') throw res;
                return {
                    key: `wave${idx + 1}`,
                    data: res.data.waveformData.waveforms.map((item) => ({ 
                        timestamp: item.timestamp, 
                        wave: item.samples,
                        status: item.status
                    }))
                };
            });

            const results = await Promise.all(waveDataPromises);
            results.forEach(result => {
                newWaveDatas[result.key] = result.data;
            });                        
            setWaveDatas(newWaveDatas);            
        }
        catch (err) { 
            console.warn(err)
        }
    }

    //Search 기준 알람 데이터를 가져오는 로직
    const getIsSearchTrendWave = async () => {
        try{
            const deviceHistory = selectBedInfo?.deviceHistory || [];
            const searchStart = moment(waveStartTime);
            const searchEnd = moment(waveEndTime);

            // 1. 환자별 입퇴원 기간 생성 (시리얼 기준)
            const periods = [];
            const sortedHistory = deviceHistory.slice().sort((a, b) => moment(a.createdAt) - moment(b.createdAt));
            let currentPeriod = null;
            sortedHistory.forEach(h => {
                if (h.action === 'ADMITTED') {
                    currentPeriod = {
                        serial: h.serial,
                        start: moment(h.createdAt),
                        end: null
                    };
                } else if (h.action === 'UNASSIGNED' && currentPeriod) {
                    currentPeriod.end = moment(h.createdAt);
                    periods.push(currentPeriod);
                    currentPeriod = null;
                }
            });
            if (currentPeriod && !currentPeriod.end) {
                currentPeriod.end = moment(); // 현재까지 입원
                periods.push(currentPeriod);
            }

            // 2. 검색 구간과 겹치는 기간만 추출
            const plans = [];
            periods.forEach(period => {
                const planStart = moment.max(searchStart, period.start);
                const planEnd = moment.min(searchEnd, period.end);
                if (planEnd.isAfter(planStart)) {
                    plans.push({
                        serial: period.serial,
                        start: planStart,
                        end: planEnd
                    });
                }
            });

            let trendWave = {
                wave1: [],
                wave2: [],
                wave3: [],
                wave4: []
            };
            for (const plan of plans) {                
                const waveDataPromises = waveParameters.map(async (item, idx) => {
                    const res = await axios.get(`/trend/waveforms?serialNo=${plan.serial}&type=${item.measureParameter}&startTime=${plan.start.utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${plan.end.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`);
                    if (res.code != '200') throw res;
                    return {
                        key: `wave${idx + 1}`,
                        data: res.data.waveformData.waveforms.map((item) => ({
                            timestamp: item.timestamp,
                            wave: item.samples,
                            status: item.status
                        }))
                    };
                });

                const results = await Promise.all(waveDataPromises);
                results.forEach(result => {
                    // key별로 데이터 병합
                    if (!trendWave[result.key]) trendWave[result.key] = [];
                    trendWave[result.key] = trendWave[result.key].concat(result.data);
                });
            }            
            setWaveDatas(trendWave);
        }
        catch (err) { 
            console.warn(err)
        }
    }
    
    //빗금 체크
    const getPatientHistories = (histories, finalWidth) => {
        if (!histories || !Array.isArray(histories) || histories.length === 0) {
            setStripedBackground(0);
            setPatientLines([]);
            return;
        }

        const startTime = moment(waveStartTime);
        const endTime = moment(waveEndTime);        

        // UNASSIGNED 리스트 필터링
        const unassignedList = histories            
            .filter(h => {
                const t = moment(h.createdAt);
                return t.isValid() && t.isSameOrAfter(startTime) && t.isSameOrBefore(endTime);
            })
            .sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());

        if (unassignedList.length === 0) {
            setStripedBackground({ left: 0, width: 0 });
            setPatientLines([]);
            return;
        }

        // 빗금 시작: 첫 UNASSIGNED
        const firstDischargeTime = moment(unassignedList[0].createdAt);
        const stripedStart = firstDischargeTime;
        const stripedEnd = endTime;
        const totalDuration = endTime.diff(startTime); // ms
        const stripedDuration = stripedEnd.diff(stripedStart); // ms

        // px 단위로 계산        
        const stripedWidthPx = ((stripedDuration / totalDuration) * finalWidth);
        setStripedBackground(stripedWidthPx);
        const lines = unassignedList
            .map(item => {
                const dischargeTime = moment(item.createdAt);
                if (!dischargeTime.isValid() || dischargeTime.isBefore(stripedStart) || dischargeTime.isAfter(stripedEnd)) return null;                
                const fromStripedStart = dischargeTime.diff(stripedStart); // ms
                return (fromStripedStart / stripedDuration) * 100;
            })
            .filter(percent => percent !== null);

        setPatientLines(lines); // 빗금 영역 내에서의 줄 위치
    };

    const handleClickDropdown = (type, val) => {
        setDropdownStates(prev => ({
          ...prev,
          [type]: typeof val !== 'undefined' ? false : !prev[type],
        }));
    };

    const handleMouseDown = (e, waveIndex) => {
        const currentAreaRef = areaRefs.current[waveIndex];
        if (!currentAreaRef) return;
        
        const bounds = currentAreaRef.getBoundingClientRect();
        const mouseX = e.clientX - bounds.left;
        const mouseY = e.clientY - bounds.top;
        setDragging(true);
        setActiveWaveIndex(waveIndex);
        setStartPosition({ x: mouseX, y: mouseY });
        setCurrentPosition({ x: mouseX, y: mouseY });
        setEndPosition(null); 
        setElapsedTime(0);
    };

    const handleMouseMove = (e) => {
        if (!dragging || activeWaveIndex === null) return;
        const currentAreaRef = areaRefs.current[activeWaveIndex];
        if (!currentAreaRef) return;

        const bounds = currentAreaRef.getBoundingClientRect();
        const mouseX = Math.max(0, Math.min(e.clientX - bounds.left, bounds.width));
        const mouseY = Math.max(0, Math.min(e.clientY - bounds.top, bounds.height));
        setCurrentPosition({ x: mouseX, y: mouseY });
        
        // 모눈종이 기준으로 시간 계산 (pixelsPerSecond = gridSizePixels / timePerGrid)
        const pixelDistance = Math.abs(mouseX - startPosition.x);
        const elapsed = (pixelDistance / pixelsPerSecond).toFixed(2);
        
        setElapsedTime(elapsed);
    };

    const handleMouseUp = () => {        
        if (!dragging) return;

        if(elapsedTime == 0){
            setDragging(false);
            setActiveWaveIndex(null);
            setEndPosition(null); 
            return;
        }        

        setDragging(false);
        setEndPosition(currentPosition); 
    };

    const handleChangeSelect = (val, index) => {
        const updatedWaveParameters = [...waveParameters];
        updatedWaveParameters[index] = {
            ...updatedWaveParameters[index],
            measureParameterId: val.id,
            measureParameter: val.parameter,
            measureParameterName: val.name
        };
        setWaveParameters(updatedWaveParameters);
    }

    const handleChangeWaveData = () => {        
        const start = moment(waveStartTime).startOf("second");
        const end = moment(waveEndTime).startOf("second");
        const newWavePoints = { wave1: [], wave2: [] }
        const newWaveYAxisRange = { wave1: { minY: -50, maxY: 200 }, wave2: { minY: -50, maxY: 200 } }        
        Object.keys(waveDatas).forEach((key, idx) => {
            const allSeconds = [];
            let current = start.clone();
            while (current <= end) {
                allSeconds.push({
                    timestamp: current.format(),
                    wave: [],
                });
                current.add(1, "second");
            }
            const mergedData = allSeconds.map((second) => {
                const found = waveDatas[key].find(
                    (item) => moment(item.timestamp).startOf("second").format() === second.timestamp
                );
                return found || second;
            });
    
            const newSegments = [];
            let currentSegment = [];
    
            mergedData.forEach((item) => {
                if (item.wave) {
                    if (item.wave.length === 0 || (!isDemoMode && !item.status)) {
                        if (currentSegment.length > 0) {
                            newSegments.push(currentSegment);
                            currentSegment = [];
                        }
                    } else {
                        const createdAtSec = moment(item.timestamp).diff(moment(waveStartTime), 'seconds');
                        item.wave.forEach((value, waveIdx) => {
                            currentSegment.push({
                                createdAtSec,
                                waveIdx,
                                waveCount: item.wave.length,
                                value,
                            });
                        });
                    }
                }
            });
    
            if (currentSegment.length > 0) {
                newSegments.push(currentSegment);
            }    
            
            // Y축 범위 계산 (-2147483648 처리 포함) 
            // ※ -2147483648 값은 페이스메이커 값으로 치환해서 처리해서 보여줘야 함. 추후 저 값이 변동되거나 했을 때는 backend와 협의 필요.
            const combinedWave = mergedData.flatMap((item, dataIndex) => 
                item.wave.map((value, waveIndex) => {
                    if (value == '-2147483648') {
                        // 같은 웨이브 내의 이전 포인트가 있으면 사용
                        if (waveIndex > 0 && item.wave[waveIndex - 1] != '-2147483648') {
                            return item.wave[waveIndex - 1];
                        }
                        // 이전 데이터 아이템의 같은 위치 포인트가 있으면 사용
                        if (dataIndex > 0 && mergedData[dataIndex - 1]?.wave[waveIndex] != '-2147483648') {
                            return mergedData[dataIndex - 1].wave[waveIndex];
                        }
                        // 다음 값이 있고 유효한 경우
                        if (waveIndex < item.wave.length - 1 && item.wave[waveIndex + 1] != '-2147483648') {
                            return item.wave[waveIndex + 1];
                        }
                        // 둘 다 없으면 0 또는 기본값 사용
                        return 0;
                    }
                    return value;
                })
            );
            const parameterName = waveParameters[idx]?.measureParameter;
            const defaultMinY = -50;
            const defaultMaxY = parameterName === 'CAPNO' ? 4500 : 200;

            let minY, maxY;

            if (combinedWave.length > 0) {
                const dataMinY = Math.min(...combinedWave);
                const dataMaxY = Math.max(...combinedWave);
                minY = Math.min(defaultMinY, dataMinY);
                maxY = Math.max(defaultMaxY, dataMaxY);
            } else {
                minY = defaultMinY;
                maxY = defaultMaxY;
            }

            // 20px 여백을 값 단위로 환산하여 Y축 범위 확장
            const paddingPixels = 20;
            const valuePerPixel = (maxY - minY) / chartHeight;
            minY = minY - paddingPixels * valuePerPixel;
            maxY = maxY + paddingPixels * valuePerPixel;

            const newPoints = newSegments.map((segment) => {
                return segment.map((pt) => {
                    let adjustedValue = pt.value;
                    
                    // -2147483648인 경우 이전/다음 값으로 대체
                    if (pt.value == '-2147483648') {
                        // 같은 세그먼트 내에서 이전 포인트 찾기
                        const currentIndex = segment.indexOf(pt);
                        if (currentIndex > 0 && segment[currentIndex - 1].value != '-2147483648') {
                            adjustedValue = segment[currentIndex - 1].value;
                        }
                        // 다음 포인트 찾기
                        else if (currentIndex < segment.length - 1 && segment[currentIndex + 1].value != '-2147483648') {
                            adjustedValue = segment[currentIndex + 1].value;
                        }
                        // 둘 다 없으면 0으로 설정
                        else {
                            adjustedValue = 0;
                        }
                    }
                    
                    const xBase = pt.createdAtSec * pixelsPerSecond;
                    const xOffset = (pt.waveIdx / (pt.waveCount - 1)) * pixelsPerSecond;
                    const x = xBase + xOffset;
                    const y = isNaN((chartHeight - ((adjustedValue - minY) / (maxY - minY)) * chartHeight)) 
                        ? chartHeight / 2 
                        : (chartHeight - ((adjustedValue - minY) / (maxY - minY)) * chartHeight);                
                    return `${x},${y}`;
                }).join(" ");
            });
            newWavePoints[`wave${idx + 1}`] = newPoints;
            newWaveYAxisRange[`wave${idx + 1}`] = { minY: minY, maxY: maxY };
        })                

        // console.log('newWavePoints :',newWavePoints)
        // console.log('newWaveYAxisRange :',newWaveYAxisRange)
        setWavePoints(newWavePoints);
        setWaveYAxisRange(newWaveYAxisRange);
    }

    // 마우스 Hover 했을때 해당 위치를 토대로 시간을 보여주는 로직
    const getTimeFromPosition = (e) => {
        if (!waveAreaContainerRef.current) return;

        // 드롭다운이 열려있으면 hoveredPosition 비활성화
        if (isSpeedDropdownOpen || Object.values(dropdownStates).some(state => state)) {
            setHoveredPosition(null);
            return;
        }
        
        const containerRect = waveAreaContainerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - containerRect.left + waveAreaContainerRef.current.scrollLeft;
        const mouseY = e.clientY - containerRect.top; // Y 좌표도 계산
        
        // 전체 차트 너비와 시간 범위
        const totalDurationSec = moment(waveEndTime).diff(moment(waveStartTime), 'seconds');
        
        // 마우스 X 위치를 시간으로 변환
        const timeOffsetSec = (mouseX / chartWidth) * totalDurationSec;
        const hoveredMoment = moment(waveStartTime).add(timeOffsetSec, 'seconds');
        
        // 차트 범위 내에서만 표시
        if (mouseX >= 0 && mouseX <= chartWidth) {
            setHoveredPosition({
                x: mouseX,
                y: mouseY,
                time: hoveredMoment
            });
        } else {
            setHoveredPosition(null);
        }
    };

    const handleMouseLeaveArea = () => {
        setHoveredPosition(null);
    };

    useEffect(() => {
        if (isTrendFull) {
            if (waveParameters.length < 4) {
                const additionalParameters = waveCodeList.filter(item => 
                    !waveParameters.some(param => param.measureParameterId === item.id)
                ).slice(0, 2);
                
                setWaveParameters(prev => [...prev, ...additionalParameters.map(item => ({
                    measureParameterId: item.id,
                    measureParameter: item.parameter,
                    measureParameterName: item.name
                }))]);
            }
        } else {
            if (waveParameters.length > 2) {
                setWaveParameters(prev => prev.slice(0, 2));
            }
        }
    }, [isTrendFull])

    useEffect(() => {        
        setDragging(false);
        setStartPosition(null);
        setCurrentPosition(null);
        setEndPosition(null);
        setElapsedTime(0);
        setActiveWaveIndex(null);
        const newTotalDurationSec = Math.abs(moment(waveStartTime).diff(moment(waveEndTime), 'seconds'));
        const calculatedWidth = newTotalDurationSec * pixelsPerSecond;

        // 25mm 표시를 위한 최소 너비 (25mm = 90px + 텍스트 여백 약 50px)
        const minWidthFor25mm = 1868;

        // chartHeight 계산 (기존 로직 유지)
        let height = 130;
        if (waveAreaContainerRef.current) {
            const parentRect = waveAreaContainerRef.current.getBoundingClientRect();
            height = (parentRect.height - 9) / waveParameters.length;
        }

        // chartWidth는 부모 너비 신경 안 쓰고, 최소 너비 이상으로만 설정
        const finalWidth = Math.max(calculatedWidth, minWidthFor25mm);
        setChartWidth(finalWidth);
        setChartHeight(height);
        getPatientHistories(patientHistories,finalWidth);
    }, [waveStartTime,waveParameters,speed]);

    useEffect(() => {
        if(moment(selectBoxTimeInfo).isValid()){
            setWaveStartTime(moment(selectBoxTimeInfo)); 
            setWaveEndTime(moment(selectBoxTimeInfo).add(30, "second"));            
        }
    }, [ selectBoxTimeInfo, waveParameters])    

    useEffect(() => {
        if(isFromSearch){
            getIsSearchTrendWave();
        }else{
            getTrendWave();
        }
    }, [waveStartTime, waveEndTime]);

    useEffect(() => {
        handleChangeWaveData();
    }, [waveDatas, pixelsPerSecond, chartHeight])
 
    //최초 Render시에만 CodeList 가져옴. Scroll 이동 
    useEffect(() => {
        getWaveCodeList()
        const handleScroll = () => {
            if (waveAreaContainerRef.current) {                
                setScrollLeft(waveAreaContainerRef.current.scrollLeft);
            }
        };
        const ref = waveAreaContainerRef.current;
        if (ref) {
            ref.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (ref) {
                ref.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const getYPosition = (value, minY, maxY, height) => {
        if (minY === maxY) return height / 2;
        return height - ((value - minY) / (maxY - minY)) * height;
    };

    return (
        <div className="relative h-full bg-[#2F2F2F]" style={{ width: 'calc(100% - 48px)' }}>
            <div className="flex justify-between items-center h-[40px] px-[20px]">
                <h2 className="text-white text-center font-medium text-lg leading-[110%] font-pretendard">{t('trend.strip')}</h2>
                <div className='flex flex-nowrap gap-[12px]'>
                    <div className="flex items-center justify-center cursor-pointer text-white rounded-[4px] border border-[#5E5E5E] bg-gradient-to-b from-[#5E5E5E] to-[#383838] p-[4px]" onClick={showStripPrint}>
                        <IconPrint className="w-[18px] h-[18px] text-white" />
                    </div>
                    {
                        isTrendFull && (
                            <div className="flex items-center justify-center cursor-pointer text-white rounded-[4px] border border-[#5E5E5E] bg-gradient-to-b from-[#5E5E5E] to-[#383838] p-[4px]" onClick={onClose}>
                                <IconArrowDown className="w-[18px] h-[18px] text-white" />
                            </div>
                        )
                    }
                </div>          
            </div>   
            <div className={`${isTrendFull ? 'h-[370px]' : 'h-[146px]'} relative scrollbar-x scrollbar-x-10px overflow-y-hidden min-w-0`} ref={waveAreaContainerRef}>    
                <div 
                    className="relative" 
                    style={{width: chartWidth}} 
                    onMouseMove={(e) => getTimeFromPosition(e)}
                    onMouseLeave={handleMouseLeaveArea}
                >             
                {
                    waveParameters.map((item,idx) => {
                        return (
                            <Fragment key={`wave-area-${idx}`}>
                                {
                                    waveDatas[`wave${idx +1}`] && (
                                        <div
                                            key={`wave-area-${idx}`}
                                            ref={(el) => (areaRefs.current[idx] = el)}
                                            className="relative w-full overflow-hidden"
                                            style={{
                                                width: chartWidth,
                                                backgroundImage: `url(${backgroundImage})`,
                                                backgroundRepeat: 'repeat',
                                            }}
                                            onMouseDown={(e) => handleMouseDown(e, idx)}
                                            onMouseMove={handleMouseMove}
                                            onMouseUp={handleMouseUp}
                                            onMouseLeave={handleMouseUp}
                                        >
                                            <div 
                                                className="absolute top-0 w-full h-full"
                                                style={{                                                     
                                                    left: `${scrollLeft}px`, 
                                                    zIndex : 99,
                                                }}
                                            >
                                                <Dropdown
                                                    type={`wave-dropdown`}
                                                    isShow={dropdownStates[`wave${idx + 1}`]}
                                                    value={waveParameters[idx]?.measureParameterId}
                                                    selectedParameters={waveParameters}
                                                    valueArr={waveCodeList}
                                                    handleClick={(val) => { handleClickDropdown(`wave${idx + 1}`, val) }}
                                                    handleValueClick={(val) => { handleChangeSelect(val, idx) }}
                                                    selectWidth={120}
                                                />
                                                {                                                    
                                                    ['IBP1', 'IBP2', 'IBP3', 'IBP4'].includes(waveParameters[idx].measureParameter) && (
                                                        <div className="w-full h-full absolute bottom-0 ml-[200px] z-10 flex flex-col items-start">
                                                            {[100, 50, 0].map((value) => {
                                                                const { minY, maxY } = waveYAxisRange[`wave${idx + 1}`] || { minY: 0, maxY: 100 };
                                                                const yPos = getYPosition(value, minY, maxY, chartHeight);
                                                                
                                                                // 값이 범위 내에 있는지 확인
                                                                const isInRange = value >= minY && value <= maxY;                                                        
                                                                
                                                                return isInRange ? (
                                                                    <div 
                                                                        key={`y-axis-${value}`}
                                                                        className='absolute flex items-center gap-[4px]'
                                                                        style={{ 
                                                                            top: `${yPos}px`,
                                                                            transform: 'translateY(-50%)'
                                                                        }}
                                                                    >
                                                                        <span className="text-white text-[14px]">{value}</span>
                                                                        <div className="w-[16px] h-[2px] bg-white"></div>
                                                                    </div>
                                                                ) : null;
                                                            })}
                                                        </div> 
                                                    )
                                                }                                                
                                            </div>           
                                            {/* {console.log(`wavePoints[wave${idx + 1}]`, wavePoints[`wave${idx + 1}`])}                                  */}
                                            {
                                                waveDatas[`wave${idx + 1}`].some(item => item.wave?.length > 0 && (isDemoMode || item.status)) ? (
                                                    <>
                                                        <svg width={chartWidth} height={chartHeight}>
                                                            {wavePoints[`wave${idx + 1}`]?.map((pointString, index) => (
                                                                <polyline
                                                                    key={index}
                                                                    points={pointString}
                                                                    fill="none"                                                                    
                                                                    stroke={parameterColors['waveParameterColors'][waveParameters[idx].measureParameter]}
                                                                    strokeWidth={1}
                                                                />
                                                            ))}
                                                        </svg> 
                                                        {/* {activeWaveIndex === idx && (dragging || endPosition) && startPosition && (
                                                            <div
                                                                className="absolute px-2 py-1 font-bold text-white rounded bg-opacity-80 bg-black"
                                                                style={{
                                                                    top: `${(startPosition.y + (endPosition?.y || currentPosition.y)) / 2}px`,
                                                                    left: `${(startPosition.x + (endPosition?.x || currentPosition.x)) / 2}px`,
                                                                    transform: "translate(-50%, -50%)",
                                                                }}
                                                            >
                                                                {elapsedTime}s
                                                            </div>
                                                        )}

                                                        {activeWaveIndex === idx && startPosition && (
                                                            <div
                                                                className="absolute w-2.5 h-2.5 rounded-full border border-white"
                                                                style={{
                                                                    top: `${startPosition.y}px`,
                                                                    left: `${startPosition.x}px`,
                                                                    transform: "translate(-50%, -50%)",
                                                                }}
                                                            />
                                                        )}

                                                        {activeWaveIndex === idx && endPosition && (
                                                            <div
                                                                className="absolute w-2.5 h-2.5 rounded-full border border-white"
                                                                style={{
                                                                    top: `${endPosition.y}px`,
                                                                    left: `${endPosition.x}px`,
                                                                    transform: "translate(-50%, -50%)"
                                                                }}
                                                            />
                                                        )}

                                                        {activeWaveIndex === idx && startPosition && (dragging || endPosition) && (
                                                            <div
                                                                className="absolute h-0 origin-top-left border-t-[1px] border-white border-dashed"
                                                                style={{
                                                                    top: startPosition.y,
                                                                    left: startPosition.x,
                                                                    width: Math.sqrt(
                                                                        ((endPosition?.x || currentPosition.x) - startPosition.x) ** 2 +
                                                                            ((endPosition?.y || currentPosition.y) - startPosition.y) ** 2
                                                                    ),
                                                                    transform: `rotate(${Math.atan2(
                                                                        (endPosition?.y || currentPosition.y) - startPosition.y,
                                                                        (endPosition?.x || currentPosition.x) - startPosition.x
                                                                    )}rad)`,
                                                                }}
                                                            />
                                                        )}                                                        */}
                                                    </>
                                                ) : (
                                                    <div className="w-[1868px] ml-[51px] pr-[16px] font-light flex items-center justify-center text-[#9F9F9F]" style={{ height: chartHeight}}>
                                                        {waveParameters[idx]?.measureParameterName != "NONE" ? t('error.noDataAvailable') : ''}
                                                    </div>
                                                )
                                            }
                                        </div>
                                    )
                                }
                            </Fragment>
                        )
                    })
                }
                <div                    
                    className={`${dragging ? 'pointer-events-none' : ''} absolute top-[5px] left-0 h-full flex`}
                    style={{   
                        zIndex : 99,                     
                        left: `${1530 + scrollLeft}px`
                    }}
                >
                    <div className="h-[20px] flex items-center mr-[20px]">
                        <div className="w-[90px] h-[9px] border border-t-0 border-l-[#FFF] border-b-[#FFF] border-r-[#FFF]"></div>
                        <p className="ml-[4px] whitespace-nowrap">25mm</p>
                    </div>
                    <div className="relative">
                        <div className="flex gap-[12px]">
                            <div className="relative top-[1px]" ref={speedDropdownRef}>
                                <div
                                    className="bg-gradient-to-b from-[#5E5E5E] to-[#383838] text-white w-[126px] h-[28px] px-[8px] rounded-[6px] cursor-pointer flex items-center justify-between border border-[#5E5E5E]"
                                    onClick={() => setIsSpeedDropdownOpen(!isSpeedDropdownOpen)}
                                    style={{ boxShadow: "0px 4px 4px 0px #00000066" }}
                                >
                                    <div style={{ fontWeight: 300 }}>{speed.label}</div>
                                </div>
                                {isSpeedDropdownOpen && (
                                    <div className="absolute scrollbar scrollbar-10px h-[90px] top-[30px] bg-[#1F1F1F] w-[126px] py-[8px] rounded-[6px] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)] z-20">
                                        {speedOptions.map((item, index) => (
                                            <div key={`speed-type-${index}`} className={`bg-[#1F1F1F] cursor-pointer h-[32px] flex items-center justify-between px-[8px] hover:bg-[#2F2F2F] ${speed.value === item.value ? "text-[#3182F7]" : "text-white"}`} onClick={(e) => { e.stopPropagation(); setSpeed(item); setIsSpeedDropdownOpen((prev) => !prev);}}>
                                                {item.label}
                                                {speed.value === item.value && <IconCheck className="w-4 h-4 ml-2" />}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="absolute transform -translate-y-1/2 pointer-events-none top-1/2 right-2">
                                    {isSpeedDropdownOpen ? <IconArrowUp /> : <IconArrowDown />}
                                </div>
                            </div>                            
                        </div>
                    </div>
                </div>
                
                {/* Select Line */}
                {/* <div 
                    className="absolute top-0 w-[1px] h-full border-l-[1px] border-dashed border-white z-[1] flex items-start" 
                    style={{ 
                        // left: `${dashLineLeftPercent}%`,
                        left: `${selectTimeLine}px`,
                    }}
                >
                    <div className="absolute w-[100px] top-10 h-[18px] p-[5px_8px] flex justify-center items-center" 
                        style={{
                            left: '50%',
                            transform: `${(selectTimeLine / chartWidth) * 100 > 3 ? 'translateX(-50%)' : 'none'}`,
                            borderRadius: '4px',
                            background: 'rgba(0, 0, 0, 0.90)'
                        }}
                    >
                        {moment(selectTimeInfo).format('HH-mm-ss')}
                    </div>
                </div>  */}

                {hoveredPosition && (
                    <div 
                        className="absolute top-0 w-[1px] h-full border-l-[1px] border-dashed border-white z-[15] flex items-start pointer-events-none" 
                        style={{ 
                            left: `${hoveredPosition.x}px`,
                        }}
                    >
                        <div 
                            className="absolute w-[100px] h-[24px] px-2 py-1 flex justify-center items-center text-xs font-medium text-white" 
                            style={{
                                left: '50%',
                                top: (() => {
                                    const containerHeight = waveAreaContainerRef.current?.clientHeight || 0;
                                    const yPercent = (hoveredPosition.y / containerHeight) * 100;
                                    
                                    // 0~50%: 마우스 아래쪽에 표시 (+50px)
                                    // 51~100%: 마우스 위쪽에 표시 (-50px)
                                    const offset = yPercent <= 50 ? 50 : -50;
                                    const calculatedTop = hoveredPosition.y + offset;
                                    
                                    // 경계 처리: 컨테이너 범위를 벗어나지 않도록
                                    return `${Math.max(12, Math.min(calculatedTop, containerHeight - 36))}px`;
                                })(),
                                transform: `${(hoveredPosition.x / chartWidth) * 100 > 90 ? 'translateX(-100%)' : (hoveredPosition.x / chartWidth) * 100 < 10 ? 'translateX(0%)' : 'translateX(-50%)'}`,
                                borderRadius: '4px',
                                background: 'rgba(0, 0, 0, 0.90)',
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            {hoveredPosition.time.format('HH:mm:ss')}
                        </div>
                    </div>
                )}

                {
                    stripedBackground > 0 && (
                        <div className="absolute top-0 right-0 h-full bg-black bg-opacity-20"
                            style={{                                
                                width : `${stripedBackground}px`,                                 
                                background : 'repeating-linear-gradient(135deg, transparent, transparent 2px, #6C6C6C80 2px, #6C6C6C80 4px)' 
                            }}
                        >           
                            {Array.isArray(patientLines) && patientLines.map((percent, idx) => (                                
                                <div 
                                    className={`absolute top-0 w-[2px] h-full bg-white`}
                                    style={{ left: `${percent}%` }}
                                    key={`patient-line-${idx}`}
                                >
                                    <div className="absolute top-0 left-[-10px] w-0 h-0 border-b-[10px] border-b-transparent border-r-[10px] border-l-[#FFF]-800"></div>
                                    <div className="absolute bottom-0 left-[-10px] w-0 h-0 border-t-[10px] border-t-transparent border-r-[10px] border-l-[#FFF]-800"></div>
                                </div>
                            ))}                               
                        </div>
                    )
                }
                </div>
            </div>            
        </div>
    ) 
};

export default Strip;
